import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Loader } from '@lucid/lego';
import { useStore } from '../../../../../store/store';
import type { Prospect } from '../../../../../api/services/lupp/prospect/dto.prospect';
import type { ProspectTestDrive } from '../../../../../api/services/lupp/prospect/dto.test-drive-schedule';
import { useUpdateProspectTestDrive } from '../../../../../api/services/lupp/prospect/test-drive-schedule';
import { formatDateTime, formatDateTimeRange } from '../../../../../utils/tools';
import { formatTestDriveId } from '../../../TestDriveBookings/utils';
import type { Theme } from '../utils';
import {
  isTestDriveModifiable,
  getCancellationReasonLabel,
  getTestDriveStatusLabel,
  formatTestDriveVehicle,
} from './utils';
import CancelAppointmentModal from './CancelAppointmentModal';
import { useTestDriveCheckIn } from './useTestDriveCheckIn';
import CheckInAction from './CheckInAction';
import styles from './TestDriveDetails.module.scss';

type TestDriveDetailsProps = {
  prospect?: Prospect;
  testDrive: ProspectTestDrive;
  theme: Theme;
  labels: Record<string, string>;
  onBack: () => void;
  onModify: () => void;
};

const TestDriveDetails: React.FC<TestDriveDetailsProps> = ({
  prospect,
  testDrive,
  theme,
  labels,
  onBack,
  onModify,
}) => {
  const locale = useStore((state) => state.locale);
  const invertedTheme = theme === 'dark' ? 'light' : 'dark';

  const { mutate: updateTestDrive, isPending } = useUpdateProspectTestDrive();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  const checkIn = useTestDriveCheckIn({
    prospect,
    testDrive,
    labels,
    onError: setErrorMessage,
  });

  useEffect(() => {
    if (!successMessage && !errorMessage) return undefined;
    const timeout = setTimeout(() => {
      setSuccessMessage('');
      setErrorMessage(null);
    }, 3000);
    return () => clearTimeout(timeout);
  }, [successMessage, errorMessage]);

  const runUpdate = (data: Partial<ProspectTestDrive>, successLabel: string) => {
    if (!prospect?.prospectIdentifier || !testDrive.testDriveScheduleId) return;
    updateTestDrive(
      { prospectId: prospect.prospectIdentifier, testDriveId: testDrive.testDriveScheduleId, data },
      {
        onSuccess: () => setSuccessMessage(successLabel),
        onError: (error: unknown) => {
          setErrorMessage(
            error instanceof Error
              ? error.message
              : (labels?.['testDrive.saveError'] ?? 'Failed to update the test drive.')
          );
        },
      }
    );
  };

  const handleConfirmCancel = (reason: string, _notes: string) => {
    // cancellationNotes and isActive aren't part of ProspectTestDriveRequest's contract: notes
    // have no BE field to land in yet (flagged as a gap to add), and isActive is deliberately left
    // server-controlled so a cancelled test drive stays visible/synced to SFDC rather than hidden.
    runUpdate(
      {
        testDriveStatus: 'cancelled',
        cancellationReason: reason,
      },
      labels?.['testDrive.cancelSuccess'] ?? 'Test drive cancelled.'
    );
    setIsCancelModalOpen(false);
  };

  const isTestDriveCompleted = testDrive.testDriveStatus === 'completed';

  return (
    <Box className={styles.wrapper}>
      {successMessage && (
        <Box className={styles.successMessageText} pt={12} pb={12} pl={16} pr={16}>
          <Typography variant="body" size="medium" color="white" theme={invertedTheme}>
            {successMessage}
          </Typography>
        </Box>
      )}
      {errorMessage && (
        <Box className={styles.errorMessageText} pt={12} pb={12} pl={16} pr={16}>
          <Typography variant="body" size="medium" color="white" theme={invertedTheme}>
            {errorMessage}
          </Typography>
        </Box>
      )}
      {isPending && (
        <Box className={styles.loaderContainer}>
          <Loader />
        </Box>
      )}

      <Box className={styles.container} theme={invertedTheme} background="gray-9">
        <Box className={styles.header}>
          <Typography variant="headline" size="small" color="white" theme={invertedTheme}>
            {formatTestDriveVehicle(testDrive.make, testDrive.model, testDrive.trim) ||
              (labels?.['testDrive.details'] ?? 'Test Drive Details')}
          </Typography>
        </Box>

        <Box className={styles.fieldRow}>
          <Box className={styles.field}>
            <Typography variant="body" size="small" color="gray-5" theme={invertedTheme}>
              {labels?.['testDrive.status'] ?? 'Status'}
            </Typography>
            <Typography variant="body" size="medium" color="white" theme={invertedTheme}>
              {getTestDriveStatusLabel(testDrive.testDriveStatus, labels)}
            </Typography>
          </Box>
          <Box className={styles.field}>
            <Typography variant="body" size="small" color="gray-5" theme={invertedTheme}>
              {labels?.['testDrive.scheduledFor'] ?? 'Scheduled For'}
            </Typography>
            <Typography variant="body" size="medium" color="white" theme={invertedTheme}>
              {formatDateTimeRange(
                testDrive.scheduledStartDatetime,
                testDrive.scheduledEndDatetime,
                locale,
                undefined,
                testDrive.timezone
              ) || '-'}
            </Typography>
          </Box>
        </Box>

        <Box className={styles.fieldRow}>
          <Box className={styles.field}>
            <Typography variant="body" size="small" color="gray-5" theme={invertedTheme}>
              {labels?.['testDrive.location'] ?? 'Location'}
            </Typography>
            <Typography variant="body" size="medium" color="white" theme={invertedTheme}>
              {testDrive.appointmentLocationName || '-'}
            </Typography>
          </Box>
          <Box className={styles.field}>
            <Typography variant="body" size="small" color="gray-5" theme={invertedTheme}>
              {labels?.['form.assignedSalesConsultant'] ?? 'Assigned Sales Consultant'}
            </Typography>
            <Typography variant="body" size="medium" color="white" theme={invertedTheme}>
              {testDrive.hostUserName || '-'}
            </Typography>
          </Box>
        </Box>

        <Box className={styles.fieldRow}>
          <Box className={styles.field}>
            <Typography variant="body" size="small" color="gray-5" theme={invertedTheme}>
              {labels?.['testDrive.completeDate'] ?? 'Complete Date'}
            </Typography>
            <Typography variant="body" size="medium" color="white" theme={invertedTheme}>
              {/* BE has no completion timestamp; the last update of a completed drive is its completion. */}
              {isTestDriveCompleted && testDrive.updatedDatetime
                ? formatDateTime(testDrive.updatedDatetime, locale, undefined, testDrive.timezone)
                : (labels?.['testDrive.notCompleted'] ?? 'Not completed yet')}
            </Typography>
          </Box>
        </Box>

        <Box className={styles.fieldRow}>
          <Box className={styles.field}>
            <Typography variant="body" size="small" color="gray-5" theme={invertedTheme}>
              {labels?.['testDrive.bookingReference'] ?? 'Booking Reference'}
            </Typography>
            <Typography variant="body" size="medium" color="white" theme={invertedTheme}>
              {formatTestDriveId(testDrive.bookingRequestId || testDrive.testDriveScheduleId)}
            </Typography>
          </Box>
        </Box>

        {testDrive.testDriveStatus === 'cancelled' && testDrive.cancellationReason && (
          <Box className={styles.fieldRow}>
            <Box className={styles.field}>
              <Typography variant="body" size="small" color="gray-5" theme={invertedTheme}>
                {labels?.['testDrive.cancellationReason'] ?? 'Cancellation Reason'}
              </Typography>
              <Typography variant="body" size="medium" color="white" theme={invertedTheme}>
                {getCancellationReasonLabel(testDrive.cancellationReason, labels)}
              </Typography>
            </Box>
          </Box>
        )}

        <CheckInAction checkIn={checkIn} theme={theme} labels={labels} />
      </Box>

      <Box className={styles.actionRow}>
        <Button
          type="secondary"
          color="neutral"
          theme={invertedTheme}
          label={labels?.['testDrive.backToList'] ?? 'Back to list'}
          handleClick={onBack}
          gaComponent="prospect-test-drive-details"
          dataTestId="test-drive-details-back-button"
        />
        <Button
          type="secondary"
          color="neutral"
          theme={invertedTheme}
          label={labels?.['testDrive.modify'] ?? 'Modify'}
          handleClick={onModify}
          disabled={!isTestDriveModifiable(testDrive)}
          gaComponent="prospect-test-drive-details"
          dataTestId="test-drive-modify-button"
        />
      </Box>

      <CancelAppointmentModal
        isOpen={isCancelModalOpen}
        theme={invertedTheme}
        labels={labels}
        isLoading={isPending}
        onKeepAppointment={() => setIsCancelModalOpen(false)}
        onConfirmCancel={handleConfirmCancel}
      />
    </Box>
  );
};

export default TestDriveDetails;
